import { useState, useMemo } from "react";

// IGV Perú: 18% = 16% Impuesto General a las Ventas + 2% Impuesto de Promoción Municipal
const TASA_IGV = 0.16;
const TASA_IPM = 0.02;
const TASA_TOTAL = TASA_IGV + TASA_IPM; 

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CalculadoraIGV() {
  const [modo, setModo] = useState("agregar"); 
  const [monto, setMonto] = useState(1000);

  const resultado = useMemo(() => {
    const valor = Math.max(0, Number(monto) || 0);

    if (modo === "agregar") {
      const valorVenta = valor;
      const igv = valorVenta * TASA_IGV;
      const ipm = valorVenta * TASA_IPM;
      const precioVenta = valorVenta + igv + ipm;
      return { valorVenta, igv, ipm, precioVenta };
    } else {
      const precioVenta = valor;
      const valorVenta = precioVenta / (1 + TASA_TOTAL);
      const igv = valorVenta * TASA_IGV;
      const ipm = valorVenta * TASA_IPM;
      return { valorVenta, igv, ipm, precioVenta };
    }
  }, [monto, modo]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";
  const botonClase = (activo) =>
    `rounded-lg border py-2.5 text-sm font-medium transition-colors ${
      activo
        ? "border-red-700 bg-red-50 text-red-800"
        : "border-slate-300 text-slate-600 hover:bg-slate-50 bg-white"
    }`;

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            🧮
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de IGV
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Tasa 18% (16% IGV + 2% IPM)</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <button type="button" onClick={() => setModo("agregar")} className={botonClase(modo === "agregar")}>
            ➕ Agregar IGV
          </button>
          <button type="button" onClick={() => setModo("extraer")} className={botonClase(modo === "extraer")}>
            ➖ Extraer IGV
          </button>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {modo === "agregar" ? "Valor de venta (sin IGV)" : "Precio de venta (con IGV incluido)"}
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  className={`${inputClase} pl-9`}
                />
              </div>
            </div>
            <p className="p-3 text-xs rounded-lg text-slate-400 bg-slate-50">
              {modo === "agregar"
                ? "Parte del monto sin impuestos y calcula cuánto debes cobrar/pagar con IGV incluido."
                : "Parte de un monto que ya incluye IGV y calcula cuánto corresponde al valor de venta y cuánto al impuesto."}
            </p>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              {modo === "agregar" ? "Precio de venta (con IGV)" : "Valor de venta (sin IGV)"}
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(modo === "agregar" ? resultado.precioVenta : resultado.valorVenta)}
            </p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Valor de venta</span>
                <span className="font-mono">{formatearSoles(resultado.valorVenta)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>+ IGV (16%)</span>
                <span className="font-mono">{formatearSoles(resultado.igv)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>+ IPM (2%)</span>
                <span className="font-mono">{formatearSoles(resultado.ipm)}</span>
              </div>
              <div className="flex justify-between pt-1.5 mt-1.5 text-white border-t border-slate-700">
                <span>Precio de venta (total)</span>
                <span className="font-mono font-bold">{formatearSoles(resultado.precioVenta)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-1 text-xs leading-relaxed text-slate-400">
          <p>1. Este cálculo es referencial y no constituye una declaración oficial ante SUNAT.</p>
          <p>
            2. La tasa vigente del IGV es 18%, compuesta por 16% de Impuesto General a las
            Ventas y 2% de Impuesto de Promoción Municipal (IPM); ambos se declaran juntos.
          </p>
          <p>
            3. Para extraer el IGV de un monto que ya lo incluye, se divide entre 1.18; para
            agregarlo a un monto sin impuestos, se multiplica por 1.18.
          </p>
          <p>4. No constituye asesoría tributaria. Verifica tu caso específico en sunat.gob.pe.</p>
        </div>
      </div>
    </div>
  );
}